import { createSlice, PayloadAction } from '@reduxjs/toolkit';

type ThemeMode = 'light' | 'dark';

const THEME_STORAGE_KEY = 'app_theme';

// Get initial theme from localStorage or system preference
const getInitialTheme = (): ThemeMode => {
  if (typeof window !== 'undefined') {
    const storedTheme = localStorage.getItem(THEME_STORAGE_KEY) as ThemeMode;
    if (storedTheme === 'dark' || storedTheme === 'light') {
      return storedTheme;
    }
    // Check system preference as fallback
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
  }
  return 'light';
};

interface ThemeState {
  theme: ThemeMode;
  isDarkMode: boolean;
}

const initialState: ThemeState = {
  theme: getInitialTheme(),
  isDarkMode: getInitialTheme() === 'dark',
};

const themeSlice = createSlice({
  name: 'theme',
  initialState,
  reducers: {
    setTheme: (state, action: PayloadAction<ThemeMode>) => {
      state.theme = action.payload;
      state.isDarkMode = action.payload === 'dark';
      // Save to localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem(THEME_STORAGE_KEY, action.payload);
        // Apply theme to document root
        const root = document.documentElement;
        if (action.payload === 'dark') {
          root.classList.add('dark-mode');
          root.classList.remove('light-mode');
        } else {
          root.classList.add('light-mode');
          root.classList.remove('dark-mode');
        }
      }
    },
    toggleTheme: (state) => {
      const newTheme = state.theme === 'light' ? 'dark' : 'light';
      state.theme = newTheme;
      state.isDarkMode = newTheme === 'dark';
      // Save to localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem(THEME_STORAGE_KEY, newTheme);
        // Apply theme to document root
        const root = document.documentElement;
        if (newTheme === 'dark') {
          root.classList.add('dark-mode');
          root.classList.remove('light-mode');
        } else {
          root.classList.add('light-mode');
          root.classList.remove('dark-mode');
        }
      }
    },
    initializeTheme: (state) => {
      // Initialize theme on app load
      const root = document.documentElement;
      if (state.theme === 'dark') {
        root.classList.add('dark-mode');
        root.classList.remove('light-mode');
      } else {
        root.classList.add('light-mode');
        root.classList.remove('dark-mode');
      }
    },
  },
});

export const { setTheme, toggleTheme, initializeTheme } = themeSlice.actions;
export default themeSlice.reducer;

